if (!customElements.get("cart-discount-form")) {
  customElements.define(
    "cart-discount-form",
    class CartDiscountForm extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.section;
        this.codes = (this.dataset.codes || "")
          .split(",")
          .map((code) => code.trim())
          .filter(Boolean);

        this.form = this.querySelector("form");
        this.input = this.querySelector("input[name=discount]");
        this.submitButton = this.querySelector("button[type=submit]");
        this.messageElement = this.querySelector(".cart-discount-message");
        this.codeList = this.querySelector(".applied-code-list");

        if (this.form) this.form.addEventListener("submit", this.onSubmit.bind(this));
        if (this.codeList) this.codeList.addEventListener("click", this.onRemove.bind(this));
      }

      onSubmit(event) {
        event.preventDefault();

        const code = (this.input.value || "").trim();
        if (!code) {
          this.input.focus();
          return;
        }

        const isApplied = this.codes.some(
          (applied) => applied.toLowerCase() === code.toLowerCase(),
        );
        if (isApplied) {
          this.showMessage(window.cartStrings.codeAlreadyApplied);
          return;
        }

        this.updateDiscount([...this.codes, code], code);
      }

      onRemove(event) {
        const button = event.target.closest(".remove-code-button");
        if (!button || !this.contains(button)) return;

        const code = (button.dataset.code || "").trim();
        this.updateDiscount(this.codes.filter((applied) => applied !== code));
      }

      async updateDiscount(codes, appliedCode = null) {
        this.hideMessage();
        this.startLoading();

        const sections = this.getSectionsToRender();
        const body = JSON.stringify({
          discount: codes.join(","),
          sections: [...new Set(sections.map((section) => section.section))],
          sections_url: window.location.pathname,
        });

        try {
          const response = await fetch(window.routes.cart_update_url, {
            ...webvista.fetchConfig(),
            body,
          });
          if (!response.ok) throw new Error(`Network error: ${response.status}`);

          const cart = await response.json();
          if (cart.error) throw new Error(cart.error);

          const rejected = (cart.discount_codes || [])
            .filter((discount) => discount && discount.applicable === false)
            .map((discount) => (discount.code || "").toLowerCase());

          if (appliedCode && rejected.includes(appliedCode.toLowerCase())) {
            this.showMessage(window.cartStrings.applyCodeError);
            await this.discardRejectedCodes();
            return;
          }

          this.codes = codes;
          if (this.input) this.input.value = "";

          webvista.popToast(
            appliedCode
              ? window.cartStrings.applyCodeSuccess
              : window.cartStrings.removeCodeSuccess,
            "success",
          );

          if (cart.sections) SectionDynamicUpdate.updateSections(sections, cart.sections);
        } catch (error) {
          this.showMessage(window.cartStrings.applyCodeError);
        } finally {
          this.endLoading();
        }
      }

      async discardRejectedCodes() {
        try {
          await fetch(window.routes.cart_update_url, {
            ...webvista.fetchConfig(),
            body: JSON.stringify({ discount: this.codes.join(",") }),
          });
        } catch (error) {
          return;
        }
      }

      getSectionsToRender() {
        return [
          {
            id: "Main-Cart",
            section: this.sectionId,
            selector: "#Main-Cart-Details",
          },
        ];
      }

      showMessage(message) {
        if (!this.messageElement) return;
        this.messageElement.textContent = message;
        this.messageElement.hidden = false;
      }

      hideMessage() {
        if (!this.messageElement) return;
        this.messageElement.textContent = "";
        this.messageElement.hidden = true;
      }

      startLoading() {
        if (!this.submitButton) return;
        this.submitButton.classList.add("loading");
        this.submitButton.setAttribute("disabled", "disabled");
      }

      endLoading() {
        if (!this.submitButton) return;
        this.submitButton.classList.remove("loading");
        this.submitButton.removeAttribute("disabled");
      }
    },
  );
}

if (!customElements.get("cart-note-editor")) {
  customElements.define(
    "cart-note-editor",
    class CartNoteEditor extends HTMLElement {
      connectedCallback() {
        this.toggle = this.querySelector(".cart-note-toggle");
        this.field = this.querySelector(".cart-note-field");
        this.text = this.querySelector(".cart-note-text");
        this.form = this.querySelector("form");
        this.textarea = this.querySelector("textarea[name=note]");
        this.submitButton = this.querySelector("button[type=submit]");

        if (this.toggle) this.toggle.addEventListener("click", this.open.bind(this));
        if (this.form) this.form.addEventListener("submit", this.onSubmit.bind(this));
      }

      open() {
        const isOpen = this.toggle.getAttribute("aria-expanded") === "true";
        if (isOpen) {
          this.close();
          return;
        }

        this.toggle.setAttribute("aria-expanded", "true");
        this.field.hidden = false;
        this.textarea.focus();
      }

      close() {
        this.toggle.setAttribute("aria-expanded", "false");
        this.field.hidden = true;
      }

      onSubmit(event) {
        event.preventDefault();
        this.startLoading();

        fetch(window.routes.cart_update_url, {
          ...webvista.fetchConfig(),
          body: JSON.stringify({ note: this.textarea.value }),
        })
          .then((response) => {
            if (!response.ok) throw new Error(`Network error: ${response.status}`);
            return response.json();
          })
          .then((cart) => {
            if (cart.error) throw new Error(cart.error);

            this.renderNote(cart.note);
            webvista.popToast(window.cartStrings.addToNoteSuccess, "success");
            this.close();
          })
          .catch(() => {
            webvista.popToast(window.cartStrings.addToNoteError, "error");
          })
          .finally(() => {
            this.endLoading();
          });
      }

      renderNote(note) {
        const value = (note || "").trim();
        this.text.textContent = value;
        this.text.hidden = value === "";
      }

      startLoading() {
        if (!this.submitButton) return;
        this.submitButton.classList.add("loading");
        this.submitButton.setAttribute("disabled", "disabled");
      }

      endLoading() {
        if (!this.submitButton) return;
        this.submitButton.classList.remove("loading");
        this.submitButton.removeAttribute("disabled");
      }
    },
  );
}
